import { useState, useCallback, useEffect, useMemo } from "react";
import { IErrorData } from "../api/services/types";
import { useFetch } from "@/common/hooks/useFetch";

export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  pages: number;
  total: number;
}

interface UsePaginatedFetchResult<T> {
  items: T[];
  totalCount: number | null;
  isLoading: boolean;
  error: IErrorData | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
}

export const usePaginatedFetch = <T>(
  fetchFunc: (page?: number) => Promise<PaginatedResponse<T>>,
  canRunRequest: boolean = true
): UsePaginatedFetchResult<T> => {
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [items, setItems] = useState<T[]>([]);

  const { data, isLoading, error, execute } = useFetch(fetchFunc, false);

  const request = async (page: number) => {
    if (page === 1) {
      setItems([]);
      setTotalCount(null);
      setPages(0);
      setPage(1);
    }

    return await execute(page);
  };

  useEffect(() => {
    if (!error && data) {
      setPage(data.page);
      setPages(data.pages);
      setTotalCount(data.total);
      setItems(prev => [...prev, ...data.items]);
    }
  }, [error, data]);

  const hasMore = useMemo(() => page && pages ? page < pages : false, [page, pages]);

  useEffect(() => {
    if (canRunRequest) {
      request(1);
    }
  }, [fetchFunc, canRunRequest]);

  const loadMore = useCallback(async () => {
    if (hasMore) {
      return await request(page + 1);
    }
  }, [fetchFunc, hasMore, page]);

  return { items, totalCount, isLoading, error, hasMore, loadMore };
};
